import { PercentPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AuthService } from "@front/m19-services";
import { StrategyService } from "@front/m19-injection-token";
import { BUSINESS_BOOST_PERCENTAGES, StrategyEx } from "@front/m19-models";
import {
  IIconComponent,
  IIntensityStepperComponent,
  IIntensityStepperOption,
  IToggleComponent,
  ITooltipDirective,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";

import { SettingRowComponent } from "../setting-row/setting-row.component";

@Component({
  selector: "app-toggle-business-boost",
  imports: [
    IToggleComponent,
    TranslocoDirective,
    IIconComponent,
    ITooltipDirective,
    SettingRowComponent,
    IIntensityStepperComponent,
  ],
  providers: [PercentPipe],
  template: `@if (spStrategy()) {
    <div class="flex flex-col gap-2" *transloco="let t">
      <app-setting-row>
        <ng-container label>
          {{ t("amazon-business-boost.title") }}
          <IIcon
            name="infoCircle"
            class="text-muted-foreground size-4"
            [iTooltip]="t('amazon-business-boost.description')"
            iTooltipPlacement="top-center"
          />
        </ng-container>
        <IToggle
          [checked]="isBusinessBoostEnabled()"
          [disabled]="isReadOnly()"
          (checkedChange)="toggleBusinessBoost()"
          [loading]="isUpdating()"
        />
      </app-setting-row>
      @if (isBusinessBoostEnabled()) {
        <IIntensityStepper
          label="common.intensity"
          [options]="intensityOptions"
          [level]="businessBoostPercentage()"
          [disabled]="isReadOnly() || isUpdating()"
          (levelChange)="updateBusinessBoost($event)"
        />
      }
    </div>
  }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleBusinessBoostComponent {
  private readonly strategyService = inject(StrategyService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly percentPipe = inject(PercentPipe);
  private readonly authService = inject(AuthService);

  public readonly strategy = input.required<StrategyEx>();
  public readonly organizationId = input.required<number>();
  public readonly isReadOnly = input.required<boolean>();
  protected readonly isUpdating = signal(false);
  protected readonly locale = toSignal(this.authService.userLocale$);

  protected readonly intensityOptions: IIntensityStepperOption<number>[] = BUSINESS_BOOST_PERCENTAGES.map(
    (percentage) => ({
      value: percentage,
      label: this.percentPipe.transform(percentage / 100, "1.0-0", this.locale()) ?? "",
    }),
  );

  protected readonly spStrategy = computed(() => {
    const strategy = this.strategy();
    return strategy.isSpStrategy() ? strategy : undefined;
  });

  protected readonly businessBoostPercentage = linkedSignal(() => this.spStrategy()?.businessBoostPercentage ?? 0);
  protected readonly isBusinessBoostEnabled = computed(() => this.businessBoostPercentage() > 0);

  toggleBusinessBoost() {
    this.updateBusinessBoost(this.isBusinessBoostEnabled() ? 0 : BUSINESS_BOOST_PERCENTAGES[0]);
  }

  protected updateBusinessBoost(percentage: number) {
    const strategy = this.spStrategy();
    if (!strategy || this.isReadOnly()) return;
    this.isUpdating.set(true);
    this.strategyService
      .updateStrategyBusinessBoost(
        strategy.accountId,
        strategy.marketplace,
        this.organizationId(),
        strategy.strategyId,
        percentage,
      )
      .subscribe({
        next: () => {
          this.toasterService.success(this.translocoService.translate("amazon-business-boost.updated"));
          this.businessBoostPercentage.set(percentage);
          this.isUpdating.set(false);
        },
        error: (error) => {
          this.toasterService.error(error, this.translocoService.translate("common.error"));
          this.isUpdating.set(false);
        },
      });
  }
}
